import { UISimulation } from '@/components/help/UISimulation';

const STEP_DURATION = 2000;

function FrameShell({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="rounded-md border border-dash-border bg-dash-bg p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-wide text-dash-muted">
          Step {step} of 8
        </div>
        <div className="text-[11px] font-mono text-dash-secondary">{title}</div>
      </div>
      {children}
    </div>
  );
}

function BrainstormInputFrame(): JSX.Element {
  return (
    <FrameShell step={1} title="Brainstorm">
      <label className="text-[11px] font-mono uppercase tracking-wide text-dash-muted">
        Topic
      </label>
      <div className="mt-1 rounded border border-dash-border bg-dash-surface px-3 py-2 text-sm text-dash-primary">
        espresso machine descaling
      </div>
      <div className="mt-3 flex gap-2">
        <span className="rounded bg-accent/15 text-accent border border-accent/40 px-2 py-1 text-[11px]">
          Generate briefs
        </span>
      </div>
    </FrameShell>
  );
}

function BriefsFrame(): JSX.Element {
  return (
    <FrameShell step={2} title="Briefs proposed">
      <ul className="space-y-2 text-xs">
        {[
          'How often should you descale?',
          'Citric vs commercial descaler',
          'Signs your machine needs descaling',
        ].map((t) => (
          <li
            key={t}
            className="rounded border border-dash-border bg-dash-surface px-3 py-2"
          >
            <div className="text-dash-primary text-sm">{t}</div>
            <div className="text-[10px] font-mono uppercase text-dash-muted mt-1">
              how-to · 900 words
            </div>
          </li>
        ))}
      </ul>
    </FrameShell>
  );
}

function ApproveFrame(): JSX.Element {
  return (
    <FrameShell step={3} title="Approve">
      <div className="rounded border border-dash-border bg-dash-surface px-3 py-2 text-xs">
        <div className="text-dash-primary text-sm">How often should you descale?</div>
        <div className="text-[10px] font-mono uppercase text-dash-muted mt-1 mb-3">
          how-to · 900 words
        </div>
        <div className="flex gap-2">
          <span className="rounded bg-accent/15 text-accent border border-accent/40 px-2 py-1 text-[11px]">
            Approve
          </span>
          <span className="rounded border border-dash-border text-dash-secondary px-2 py-1 text-[11px]">
            Edit
          </span>
          <span className="rounded border border-dash-border text-dash-muted px-2 py-1 text-[11px]">
            Reject
          </span>
        </div>
      </div>
    </FrameShell>
  );
}

function PipelineFrame(): JSX.Element {
  return (
    <FrameShell step={4} title="Pipeline running">
      <div className="rounded border border-warning/40 bg-warning/10 px-3 py-2">
        <div className="flex items-center justify-between">
          <div className="text-sm text-dash-primary">
            How often should you descale?
          </div>
          <span className="text-[10px] font-mono uppercase tracking-wide text-warning">
            reviewing · iter 2
          </span>
        </div>
        <div className="mt-2 h-1 rounded bg-dash-bg overflow-hidden">
          <div className="h-full w-2/3 bg-warning/60" />
        </div>
      </div>
    </FrameShell>
  );
}

function DraftFrame(): JSX.Element {
  return (
    <FrameShell step={5} title="Draft ready">
      <div className="rounded border border-dash-border bg-dash-surface px-3 py-2">
        <div className="flex items-center justify-between">
          <div className="text-sm text-dash-primary">
            How often should you descale?
          </div>
          <span className="rounded bg-success/15 text-success border border-success/40 px-2 py-0.5 text-[10px] uppercase font-mono">
            draft · 8.4
          </span>
        </div>
        <div className="text-xs text-dash-secondary mt-1">
          903 words · 5 sections · 3 illustrations
        </div>
      </div>
    </FrameShell>
  );
}

function EditorFrame(): JSX.Element {
  return (
    <FrameShell step={6} title="Editor">
      <div className="rounded border border-dash-border bg-dash-surface px-3 py-2">
        <div className="text-sm text-dash-primary">## Why descaling matters</div>
        <div className="mt-1 text-xs text-dash-secondary">
          Limescale builds up inside the boiler and starts to{' '}
          <span className="underline decoration-accent decoration-dotted">choke flow</span>
          {' '}within a few months of city water use.
        </div>
        <div className="mt-2 rounded border border-dash-border bg-dash-bg p-2">
          <div className="text-[10px] font-mono uppercase text-dash-muted">
            [Illustration]
          </div>
          <div className="text-xs text-dash-primary mt-1">flow-restriction-diagram.svg</div>
        </div>
      </div>
    </FrameShell>
  );
}

function AnnotationsFrame(): JSX.Element {
  return (
    <FrameShell step={7} title="Annotate">
      <div className="grid grid-cols-[1fr_120px] gap-2">
        <div className="rounded border border-dash-border bg-dash-surface px-3 py-2">
          <div className="text-sm text-dash-primary">## Why descaling matters</div>
          <div className="mt-1 text-xs text-dash-secondary">
            Limescale builds up inside the boiler and{' '}
            <span className="underline decoration-violet-400 decoration-2">
              starts to choke flow
            </span>
            .
          </div>
        </div>
        <div className="rounded border border-violet-400/40 bg-violet-400/10 px-2 py-2 text-[11px] text-dash-primary">
          <div className="font-semibold text-violet-300">Tighten claim</div>
          <div className="text-dash-secondary">Add a measurement.</div>
        </div>
      </div>
    </FrameShell>
  );
}

function PublishedFrame(): JSX.Element {
  return (
    <FrameShell step={8} title="Published">
      <div className="rounded border border-success/40 bg-success/10 px-3 py-2">
        <div className="text-sm text-dash-primary">
          How often should you descale?
        </div>
        <div className="text-xs text-dash-secondary mt-1">
          Live at home-espresso.pages.dev/articles/descaling-frequency
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className="rounded bg-success/15 text-success border border-success/40 px-2 py-0.5 text-[10px] uppercase font-mono">
            live
          </span>
          <span className="text-[11px] font-mono text-dash-muted">deploy 12s</span>
        </div>
      </div>
    </FrameShell>
  );
}

export function EndToEndSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        {
          frame: <BrainstormInputFrame />,
          label: 'Type a topic',
          duration: STEP_DURATION,
        },
        { frame: <BriefsFrame />, label: 'AI proposes briefs', duration: STEP_DURATION },
        { frame: <ApproveFrame />, label: 'Approve a brief', duration: STEP_DURATION },
        { frame: <PipelineFrame />, label: 'Pipeline writes + reviews', duration: STEP_DURATION },
        { frame: <DraftFrame />, label: 'Draft lands', duration: STEP_DURATION },
        { frame: <EditorFrame />, label: 'Edit inline', duration: STEP_DURATION },
        { frame: <AnnotationsFrame />, label: 'Annotate revisions', duration: STEP_DURATION },
        { frame: <PublishedFrame />, label: 'Publish to Cloudflare', duration: STEP_DURATION },
      ]}
    />
  );
}
